import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { MenuItemUnavailable } from 'features/menu/components/menu-item-unavailable';
import { MenuProductSkeleton } from 'features/menu/components/menu-product-skeleton';
import { useFetchMainMenuOnMount } from 'features/menu/hooks/use-fetch-main-menu';
import { useMenuScroll } from 'features/menu/hooks/use-menu-scroll';
import { SHOULD_CHECK_ITEM_IN_OFFER } from 'features/menu/pages/main-menu-page/constants';
import { useHandleMenuItemInOffer } from 'features/menu/pages/menu-object-page/use-handle-menu-item-in-offer';
import { useLogMenuContent } from 'features/menu/pages/menu-object-page/use-log-menu-content';

import { MenuObject, SanityMenuObject } from '@rbi-ctg/menu';
import { CardModal } from 'components/card-modal';
import { ProductDetail } from 'components/product-detail-v2';
import { PageAnnouncer } from 'components/ui/page-announcer';
import { MenuObjectTypes } from 'enums/menu';
import { MenuDayPartUnavailable } from 'experiments/deeplinks-flow-feature/treatment/features/menu/components/menu-day-part-unavailable';
import { findLegacyParentSectionId } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/find-legacy-parent-section-id';
import { isLegacyMenuObjectOutOfActiveDayParts } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/is-legacy-menu-object-out-of-active-day-parts';
import { navigateToUnavailableFullMenu } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/navigate-to-unavailable-full-menu';
import { navigateToUnavailableMenuSection } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/navigate-to-unavailable-menu-section';
import { resolveLegacyUnavailableSectionRedirect } from 'experiments/deeplinks-flow-feature/treatment/features/menu/utils/resolve-legacy-unavailable-section-redirect';
import {
  useDeeplinksFlowExperiment,
  useExposeDeeplinksFlowExperiment,
} from 'experiments/deeplinks-flow-feature/use-deeplinks-flow-experiment';
import { getStaticPathname } from 'navigation/location/location';
import { useMenuContext } from 'state/menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useGetMenuObjectQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-query';
import { ProductWizardProvider } from 'state/product-wizard-v2';
import { ProductMenuObject } from 'state/product-wizard-v2/types';
import { IMenuObjectWithDaypart } from 'utils/availability';
import { getMenuItemDayParts } from 'utils/availability/daypart-availability';
import { routes } from 'utils/routing';

export const MenuProduct = () => {
  const pathname = getStaticPathname();
  const router = useRouter();
  const { id, menuObjectType } = useLocalSearchParams<{
    id: string;
    menuObjectType: MenuObjectTypes;
  }>();

  useMenuScroll();
  // Ensure Sanity main menu sections are loaded so we can resolve the parent section.
  const isMainMenuMounting = useFetchMainMenuOnMount();
  const {
    isEnabled: isDeeplinksFlowEnabled,
    isLoading: isExperimentLoading,
  } = useDeeplinksFlowExperiment();

  const { data, loading: menuObjectLoading, error: menuObjectError } = useGetMenuObjectQuery({
    sanityId: id,
    menuObjectType,
  });

  const { isOffer, setIsOffer, filterForAvailability } = useMenuContext();
  const {
    data: mainMenuData,
    isMenuLoading,
    storeMenuCalled,
    storeMenuLoading,
    activeDayParts,
    isDayPartsSourceResolved,
  } = useMainMenuContext();
  const menuObjectData = filterForAvailability(data as SanityMenuObject) as MenuObject | null;
  const rawMenuObject = data as IMenuObjectWithDaypart | undefined;
  const rawMenuObjectDayParts = rawMenuObject ? getMenuItemDayParts(rawMenuObject) : [];
  const isOutOfDayPart = isLegacyMenuObjectOutOfActiveDayParts({
    activeDayParts,
    entry: rawMenuObject,
    isDayPartsSourceResolved,
  });

  useLogMenuContent({
    loggedValues: {
      id: menuObjectData?._id || '',
      name: menuObjectData?.name?.locale || '',
      menuType: menuObjectData?._type || '',
    },
    pathname,
  });

  const handleMenuItemInOffer = useHandleMenuItemInOffer(setIsOffer);

  React.useEffect(() => {
    if (!!data && SHOULD_CHECK_ITEM_IN_OFFER) {
      handleMenuItemInOffer(data);
    }
  }, [handleMenuItemInOffer, data]);

  const pageTitle = `${menuObjectData?.name?.locale} - Burger King`;

  // Wait for store pricing/availability before deciding the item is unavailable —
  // otherwise filterForAvailability(null) races and redirects available items away.
  const isStoreAvailabilityReady = storeMenuCalled && !storeMenuLoading && !isMenuLoading;
  const isUnavailable =
    !menuObjectLoading && isStoreAvailabilityReady && (!menuObjectData || !!menuObjectError);

  useExposeDeeplinksFlowExperiment(isUnavailable && !isExperimentLoading);

  const [didAttemptSectionRedirect, setDidAttemptSectionRedirect] = useState(false);
  const didNavigateRef = useRef(false);

  // Main-menu fetch finished (success or empty/failed). Do not require options —
  // a missing Menu.options must still escape the skeleton into Tap-here UI.
  const mainMenuSettled = !isMainMenuMounting && !isMenuLoading;
  const menuSectionsReady = mainMenuSettled && !!mainMenuData?.Menu?.options;

  const sectionIdForUnavailable = useMemo(() => {
    if (!isUnavailable || !id || !menuSectionsReady) {
      return undefined;
    }
    const menuSections = (mainMenuData?.Menu?.options ?? []) as Array<{
      _id?: string;
      _type?: string;
      options?: Array<{ _id?: string; _type?: string; options?: unknown }>;
    }>;
    return findLegacyParentSectionId(menuSections, id);
  }, [id, isUnavailable, mainMenuData?.Menu?.options, menuSectionsReady]);

  const handleChooseAnotherRestaurant = useCallback(() => {
    const typeSegment = String(menuObjectType || 'item').toLowerCase();
    router.navigate({
      pathname: routes.minimalStoreLocator,
      params: {
        redirectUrl: `${routes.menu}/${typeSegment}/${id}`,
        unavailableItemId: id,
      },
    });
  }, [id, menuObjectType, router]);

  useEffect(() => {
    didNavigateRef.current = false;
    setDidAttemptSectionRedirect(false);
  }, [id]);

  useEffect(() => {
    if (!isDeeplinksFlowEnabled || !isUnavailable || !id || didNavigateRef.current) {
      return;
    }

    if (isOutOfDayPart) {
      setDidAttemptSectionRedirect(true);
      return;
    }

    const action = resolveLegacyUnavailableSectionRedirect({
      mainMenuSettled,
      sectionIdForUnavailable,
    });

    if (action === 'wait') {
      return;
    }

    if (action === 'navigate' && sectionIdForUnavailable) {
      didNavigateRef.current = true;
      navigateToUnavailableMenuSection(router, sectionIdForUnavailable, id);
      return;
    }

    // No parent section: let Full Menu own the unavailable product recovery.
    didNavigateRef.current = true;
    navigateToUnavailableFullMenu(router, { itemId: id });
  }, [
    id,
    isDeeplinksFlowEnabled,
    isOutOfDayPart,
    isUnavailable,
    mainMenuSettled,
    router,
    sectionIdForUnavailable,
  ]);

  if (
    menuObjectLoading ||
    !isStoreAvailabilityReady ||
    (isUnavailable && isExperimentLoading) ||
    (isDeeplinksFlowEnabled &&
      isUnavailable &&
      !isOutOfDayPart &&
      (!didAttemptSectionRedirect || sectionIdForUnavailable))
  ) {
    return (
      <CardModal alwaysHideHeader headerText={pageTitle} noDesktopWebMaxWidth>
        <MenuProductSkeleton type={menuObjectType} />
      </CardModal>
    );
  }

  if (isUnavailable || !menuObjectData) {
    if (isDeeplinksFlowEnabled && isOutOfDayPart) {
      return (
        <CardModal alwaysHideHeader headerText={pageTitle} noDesktopWebMaxWidth>
          <MenuDayPartUnavailable kind="item" dayParts={rawMenuObjectDayParts} />
        </CardModal>
      );
    }

    return (
      <CardModal alwaysHideHeader headerText={pageTitle} noDesktopWebMaxWidth>
        <MenuItemUnavailable
          isOffer={isOffer}
          onChooseAnotherRestaurant={
            isDeeplinksFlowEnabled ? handleChooseAnotherRestaurant : undefined
          }
        />
      </CardModal>
    );
  }

  return (
    <CardModal
      testID="product-detail"
      alwaysHideHeader
      headerText={pageTitle}
      noDesktopWebMaxWidth
      hasInternalFooter
    >
      <ProductWizardProvider product={menuObjectData as ProductMenuObject}>
        <PageAnnouncer pageTitle={menuObjectData.name.locale} />
        <ProductDetail />
      </ProductWizardProvider>
    </CardModal>
  );
};
